import { clamp, Nullable } from '@ps/utils';
import { Option } from '@sapphire/result';
import {
  ALLOCATION_UNIT,
  type AllocationUnit,
} from '../../constants/survey-quotas.constants';
import { completesToClick } from '../../converters/completes-to-click';

// Shared by the quota drawer (row-by-row editing) and SurveyQuotasManagerService's own
// redistribution (survey/ui's services/survey-quotas-manager) — both need the same allocation math, and
// neither may import the other's own domain layer, so it lives here instead.
export function sumBy<T>(
  items: readonly T[],
  value: (item: T) => number,
): number {
  return items.reduce((sum, item) => sum + value(item), 0);
}

// Inverse of a percentage-of-total calculation — the raw, independently-rounded number a
// percentage projects to against a total. Raw because rounding each row independently this way
// can leave a whole set's numbers summing away from totalCompletes; the caller trues that up
// across sibling rows when it matters (e.g. censusNumber in the drawer).
export function numberFromPercentage(
  percentage: number,
  totalCompletes: number,
): number {
  return Math.round((percentage * totalCompletes) / 100);
}

// What every quota on the Define/Edit Audience page is allocated against: completes normally,
// but the derived click count once the buyer switches the metadata section's Completes/Clicks
// toggle on. Legacy expressed the same thing as `complete = totalClicks || numberOfCompletes`,
// with totalClicks 0 whenever click balancing was off (SurveyClicksCalculator).
export interface QuotaAllocationBasis {
  readonly completes: Nullable<number>;
  readonly incidence: Nullable<number>;
  readonly clickBalancing: boolean;
}

// Value equality for the basis. Both audience pages hold it in a signal rebuilt on every
// metadata edit (title, LOI, tags…), so without this each keystroke would notify where the
// single number it replaced did not. Lives beside the type so a fourth field can't be added to
// the comparison on one page and forgotten on the other.
export function isSameAllocationBasis(
  previous: QuotaAllocationBasis,
  current: QuotaAllocationBasis,
): boolean {
  return (
    previous.completes === current.completes &&
    previous.incidence === current.incidence &&
    previous.clickBalancing === current.clickBalancing
  );
}

export function allocationUnitFor(clickBalancing: boolean): AllocationUnit {
  return clickBalancing ? ALLOCATION_UNIT.CLICKS : ALLOCATION_UNIT.COMPLETES;
}

// Nothing entered yet — the state both audience pages and SurveyQuotasManagerService start from,
// kept here so the three never drift apart.
export const INITIAL_QUOTA_ALLOCATION_BASIS: QuotaAllocationBasis = {
  completes: null,
  incidence: null,
  clickBalancing: false,
};

// Clicks are undefined without an incidence to divide by, so an incomplete basis falls back to
// completes rather than producing Infinity — the toggle itself is disabled until both are set,
// this only covers the window before that.
export function quotaAllocationTotal(
  basis: QuotaAllocationBasis,
): Option<number> {
  const { completes, incidence, clickBalancing } = basis;
  if (!completes || !clickBalancing || !incidence) {
    return Option.from(completes);
  }
  return Option.some(completesToClick(completes, incidence));
}

// Largest-remainder apportionment: stays non-negative and sums exactly to `total` for any number
// of rows, unlike rounding each row independently. Shared by multiselect's census distribution
// and the quota manager's own rescale-on-completes-change.
export function largestRemainderCounts(
  weights: readonly number[],
  total: number,
): number[] {
  const weightTotal = sumBy(weights, (weight) => weight);
  if (!weightTotal) {
    return weights.map(() => 0);
  }

  const exactShares = weights.map((weight) => (weight / weightTotal) * total);
  const flooredShares = exactShares.map((share) => Math.floor(share));
  const deficit = total - sumBy(flooredShares, (share) => share);
  const remainderOrder = flooredShares
    .map((_, index) => index)
    .sort(
      (first, second) =>
        exactShares[second] -
        flooredShares[second] -
        (exactShares[first] - flooredShares[first]),
    );

  const counts = [...flooredShares];
  for (let index = 0; index < deficit; index++) {
    counts[remainderOrder[index]] += 1;
  }
  return counts;
}

// The single "too many rows for the allocation" check nesting needs, shared by the drawer's own
// live NestingGroupData and the committed, flat Quota[] the survey re-derives it from — each maps
// its own group shape down to plain quota arrays and calls this rather than repeating the math.
export function groupsExceedAllocation<T>(
  groups: readonly (readonly T[])[],
  totalAllocation: number,
): boolean {
  return groups.some((quotas) => quotas.length > totalAllocation);
}

export function flexibleBounds(
  value: number,
  flexible: { enable: boolean; value: Nullable<number> },
  totalCompletes: number,
): { minimum: number; maximum: number } {
  if (!flexible.enable) {
    return { minimum: value, maximum: value };
  }
  const spread = Math.round((value * (flexible.value ?? 0)) / 100);
  return {
    minimum: clamp(value - spread, 0, totalCompletes),
    maximum: clamp(value + spread, 0, totalCompletes),
  };
}
